import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useRole } from "@/lib/roleContext";
import { toast } from "sonner";
import { Search, UserPlus, Check, Undo2, QrCode, X, Camera, ScanLine } from "lucide-react";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import QRScanner from "@/components/QRScanner";
import { formatDate } from "@/lib/format";

const STATUS_LABEL = {
    unpaid: "Advance",
    paid: "Paid externally",
    pay_at_door: "Pay at door",
};

export default function CheckIn() {
    const { eventId: routeEventId } = useParams();
    const navigate = useNavigate();
    const { activeArtistId } = useRole();
    const [events, setEvents] = useState([]);
    const [eventId, setEventId] = useState(routeEventId || "");
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");
    const [walkinOpen, setWalkinOpen] = useState(false);
    const [walkin, setWalkin] = useState({ fan_name: "", fan_email: "", ticket_category_name: "General", payment_status: "pay_at_door" });

    // Scanner state
    const [scanOpen, setScanOpen] = useState(false);
    const [scanResult, setScanResult] = useState(null); // {result, message, registration, event}
    const [scanning, setScanning] = useState(false);
    const [lastToken, setLastToken] = useState("");

    // Load list of events for this artist
    useEffect(() => {
        if (!activeArtistId) return;
        endpoints.workspaceEvents(activeArtistId).then((d) => {
            const list = [...d.grouped.upcoming, ...d.grouped.past];
            setEvents(list);
            if (!eventId && list.length > 0) setEventId(list[0].id);
        });
    }, [activeArtistId]);

    const load = () => {
        if (!eventId) return;
        endpoints
            .eventAttendees(eventId, q ? { q } : {})
            .then(setData)
            .catch((err) => {
                const code = err?.response?.status;
                if (code === 403) {
                    setData({ stats: { total_registrations: 0, checked_in: 0, capacity: 0 }, attendees: [], forbidden: true });
                } else {
                    setData({ stats: { total_registrations: 0, checked_in: 0, capacity: 0 }, attendees: [], error: true });
                }
            });
    };
    useEffect(load, [eventId, q]);

    const doCheckIn = async (reg) => {
        try {
            await endpoints.checkIn(eventId, reg.id);
            toast.success(`${reg.fan_name} checked in`);
            load();
        } catch { toast.error("Check-in failed"); }
    };
    const undo = async (reg) => {
        try {
            await endpoints.undoCheckIn(eventId, reg.id);
            toast(`Undone: ${reg.fan_name}`);
            load();
        } catch { toast.error("Undo failed"); }
    };

    const submitWalkin = async () => {
        if (!walkin.fan_name.trim()) { toast.error("Name required"); return; }
        try {
            await endpoints.addWalkin(eventId, walkin);
            toast.success(`Walk-in added: ${walkin.fan_name}`);
            setWalkinOpen(false);
            setWalkin({ fan_name: "", fan_email: "", ticket_category_name: "General", payment_status: "pay_at_door" });
            load();
        } catch { toast.error("Could not add walk-in"); }
    };

    // Scan handling — debounce duplicate token processing
    const handleScan = async (rawToken) => {
        if (!eventId || scanning) return;
        const token = String(rawToken || "").trim();
        if (!token || token === lastToken) return;
        setLastToken(token);
        setScanning(true);
        try {
            const res = await endpoints.scanQR(eventId, token);
            setScanResult(res);
            load(); // Refresh list on success
            // Play a subtle audible cue would be nice, but skip for MVP
        } catch {
            setScanResult({ result: "invalid_token", message: "Scan failed. Try again." });
        } finally {
            setScanning(false);
            // Allow same token to be re-scanned after a few seconds
            setTimeout(() => setLastToken(""), 3000);
        }
    };

    const closeScanResult = () => setScanResult(null);
    const openScanner = () => {
        setScanResult(null);
        setLastToken("");
        setScanOpen(true);
    };

    const updateWalkinPayment = async (reg, newStatus) => {
        try {
            await endpoints.updatePaymentStatus(reg.id, newStatus);
            toast.success("Payment status updated");
            load();
        } catch { toast.error("Update failed"); }
    };

    const currentEvent = events.find((e) => e.id === eventId);
    const isPayAtDoor = !!currentEvent?.is_pay_at_door;

    return (
        <div className="pm-fade" data-testid="checkin-page">
            <header className="border-b border-[#2a2a2a] px-4 md:px-8 py-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">Check-in</div>
                <h1 className="font-display font-black uppercase tracking-tighter text-3xl md:text-4xl">Door</h1>

                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-3">
                    <select
                        value={eventId}
                        onChange={(e) => {
                            setEventId(e.target.value);
                            navigate(`/workspace/check-in/${e.target.value}`, { replace: true });
                        }}
                        className="bg-transparent border border-[#2a2a2a] px-3 py-2 font-mono uppercase text-sm focus:border-[#ff4400] outline-none flex-1"
                        data-testid="checkin-event-select"
                    >
                        <option value="" className="bg-[#141414]">Select event…</option>
                        {events.map((e) => (
                            <option key={e.id} value={e.id} className="bg-[#141414]">
                                {formatDate(e.date)} · {e.name}
                            </option>
                        ))}
                    </select>
                    <button
                        onClick={openScanner}
                        className="pm-btn pm-btn-primary md:w-auto w-full text-base py-3 md:py-2"
                        data-testid="checkin-scan-qr-btn"
                        disabled={!eventId}
                    >
                        <ScanLine className="w-5 h-5 mr-2" /> Scan QR
                    </button>
                </div>
            </header>

            {data && (
                <>
                    <div className="grid grid-cols-3 gap-px bg-[#2a2a2a] border-b border-[#2a2a2a]">
                        <StatBox label="Registered" value={data.stats.total_registrations} testId="stat-registered" />
                        <StatBox label="Checked in" value={data.stats.checked_in} highlight testId="stat-checked-in" />
                        <StatBox label="Capacity" value={data.stats.capacity} testId="stat-capacity" />
                    </div>

                    <div className="px-4 md:px-8 py-4 flex flex-col md:flex-row gap-3 sticky top-14 md:top-0 bg-[#0a0a0a] z-20 border-b border-[#2a2a2a]">
                        <div className="flex items-center gap-2 border border-[#2a2a2a] px-3 py-2 flex-1">
                            <Search className="w-4 h-4 text-[#666]" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search by name or email…"
                                className="flex-1 bg-transparent outline-none text-sm"
                                data-testid="checkin-search-input"
                            />
                        </div>
                        <Dialog open={walkinOpen} onOpenChange={setWalkinOpen}>
                            <DialogTrigger asChild>
                                <button className="pm-btn" data-testid="checkin-add-walkin">
                                    <UserPlus className="w-4 h-4 mr-2" /> Walk-in
                                </button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-md">
                                <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">Add walk-in</DialogTitle></DialogHeader>
                                <div className="space-y-3">
                                    <input placeholder="Name" value={walkin.fan_name} onChange={(e) => setWalkin({ ...walkin, fan_name: e.target.value })} className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none" data-testid="walkin-name-input" />
                                    <input placeholder="Email (optional)" value={walkin.fan_email} onChange={(e) => setWalkin({ ...walkin, fan_email: e.target.value })} className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none" data-testid="walkin-email-input" />
                                    <input placeholder="Tier (optional)" value={walkin.ticket_category_name} onChange={(e) => setWalkin({ ...walkin, ticket_category_name: e.target.value })} className="w-full bg-transparent border-b border-[#2a2a2a] py-2 focus:border-[#ff4400] outline-none" data-testid="walkin-tier-input" />
                                    <select value={walkin.payment_status} onChange={(e) => setWalkin({ ...walkin, payment_status: e.target.value })} className="w-full bg-transparent border border-[#2a2a2a] px-3 py-2 focus:border-[#ff4400] outline-none font-mono uppercase text-sm" data-testid="walkin-payment-select">
                                        <option value="paid" className="bg-[#141414]">Paid</option>
                                        <option value="pay_at_door" className="bg-[#141414]">Pay at door</option>
                                        <option value="unpaid" className="bg-[#141414]">Unpaid</option>
                                    </select>
                                </div>
                                <DialogFooter>
                                    <button className="pm-btn" onClick={() => setWalkinOpen(false)}>Cancel</button>
                                    <button className="pm-btn pm-btn-primary" onClick={submitWalkin} data-testid="walkin-submit">Add & check in</button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>

                    <div className="border-t border-[#2a2a2a]">
                        {data.attendees.length === 0 ? (
                            <div className="border-b border-[#2a2a2a] p-10 text-center text-[#a3a3a3]" data-testid="checkin-empty">
                                No attendees match.
                            </div>
                        ) : data.attendees.map((r) => (
                            <div key={r.id} className={`flex items-center gap-3 md:gap-4 p-4 border-b border-[#2a2a2a] ${r.checked_in ? "bg-[#0e1a10]" : ""}`} data-testid={`checkin-row-${r.id}`}>
                                <div className={`w-10 h-10 flex items-center justify-center font-mono uppercase text-sm border ${r.checked_in ? "border-[#00e676] text-[#00e676]" : "border-[#2a2a2a] text-[#f4f4f0]"}`}>
                                    {r.fan_name.charAt(0)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-bold uppercase truncate">{r.fan_name}{r.is_walkin && <span className="ml-2 text-[10px] font-mono text-[#ff4400]">WALK-IN</span>}</div>
                                    <div className="text-[10px] font-mono uppercase text-[#666] truncate">
                                        {r.ticket_category_name || "—"} · {STATUS_LABEL[r.payment_status] || r.payment_status}
                                    </div>
                                </div>
                                {isPayAtDoor && (
                                    <select
                                        value={r.payment_status}
                                        onChange={(e) => updateWalkinPayment(r, e.target.value)}
                                        className="hidden md:block bg-transparent border border-[#2a2a2a] px-2 py-1 text-[10px] font-mono uppercase focus:border-[#ff4400] outline-none"
                                        data-testid={`row-payment-select-${r.id}`}
                                    >
                                        <option value="pay_at_door" className="bg-[#141414]">Unpaid at door</option>
                                        <option value="paid" className="bg-[#141414]">Paid at door</option>
                                    </select>
                                )}
                                {r.checked_in ? (
                                    <button onClick={() => undo(r)} className="pm-btn text-xs" data-testid={`checkin-undo-${r.id}`}>
                                        <Undo2 className="w-3 h-3 mr-1" /> Undo
                                    </button>
                                ) : (
                                    <button onClick={() => doCheckIn(r)} className="pm-btn pm-btn-primary text-xs" data-testid={`checkin-btn-${r.id}`}>
                                        <Check className="w-3 h-3 mr-1" /> Check in
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </>
            )}

            {/* Scanner dialog */}
            <Dialog open={scanOpen} onOpenChange={setScanOpen}>
                <DialogContent
                    className="bg-[#0a0a0a] border-[#2a2a2a] rounded-none max-w-lg p-0 overflow-hidden"
                    data-testid="scan-dialog"
                >
                    <DialogHeader className="px-5 pt-5">
                        <DialogTitle className="font-display uppercase tracking-tight text-2xl flex items-center gap-2">
                            <ScanLine className="w-6 h-6 text-[#ff4400]" /> Scan attendee pass
                        </DialogTitle>
                    </DialogHeader>

                    <div className="px-5 pb-5">
                        {!scanResult ? (
                            <QRScanner active={scanOpen} onScan={handleScan} onManualSubmit={handleScan} />
                        ) : (
                            <ScanResult
                                result={scanResult}
                                isPayAtDoor={isPayAtDoor}
                                onPaymentUpdate={updateWalkinPayment}
                                onScanNext={() => { setScanResult(null); setLastToken(""); }}
                                onClose={() => { setScanOpen(false); closeScanResult(); }}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ScanResult({ result, isPayAtDoor, onPaymentUpdate, onScanNext, onClose }) {
    const kind = result.result;
    const isOk = kind === "ok";
    const isDup = kind === "already_checked_in";
    const isWrong = kind === "wrong_event";
    const isInvalid = kind === "invalid_token";
    const isCancelled = kind === "cancelled";

    const themeMap = {
        ok: { border: "border-[#00e676]", text: "text-[#00e676]", label: "Checked in", testId: "scan-result-ok" },
        already_checked_in: { border: "border-[#ffea00]", text: "text-[#ffea00]", label: "Already checked in", testId: "scan-result-duplicate" },
        wrong_event: { border: "border-[#ff3333]", text: "text-[#ff3333]", label: "Wrong event", testId: "scan-result-wrong-event" },
        invalid_token: { border: "border-[#ff3333]", text: "text-[#ff3333]", label: "Invalid pass", testId: "scan-result-invalid" },
        cancelled: { border: "border-[#ff3333]", text: "text-[#ff3333]", label: "Cancelled", testId: "scan-result-cancelled" },
    };
    const t = themeMap[kind] || themeMap.invalid_token;

    return (
        <div className={`border-2 ${t.border} p-5`} data-testid={t.testId}>
            <div className={`text-[10px] font-mono uppercase tracking-widest ${t.text}`}>{t.label}</div>
            <div className="mt-1 font-display font-black uppercase text-2xl" data-testid="scan-result-message">
                {result.message}
            </div>

            {result.registration && (
                <div className="mt-4 space-y-2">
                    <Row label="Fan" value={result.registration.fan_name} testId="scan-fan-name" />
                    <Row label="Tier" value={result.registration.ticket_category_name || "General"} />
                    <Row label="Payment" value={STATUS_LABEL[result.registration.payment_status] || result.registration.payment_status} />
                    {result.registration.checked_in_at && (
                        <Row label={isDup ? "Original check-in" : "Check-in time"} value={new Date(result.registration.checked_in_at).toLocaleString()} testId="scan-checkin-time" />
                    )}
                    <Row label="Status" value={result.registration.checked_in ? "Checked in" : (result.registration.status || "Confirmed")} />
                </div>
            )}

            {result.wrong_event && (
                <div className="mt-3 text-xs text-[#a3a3a3]" data-testid="scan-wrong-event-details">
                    Belongs to: <span className="text-[#f4f4f0] font-display font-bold uppercase">{result.wrong_event.name}</span>
                </div>
            )}

            {isPayAtDoor && isOk && result.registration && (
                <div className="mt-4 border-t border-[#2a2a2a] pt-3">
                    <div className="text-[10px] font-mono uppercase text-[#666] mb-2">Update payment</div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => onPaymentUpdate(result.registration, "paid")}
                            className="pm-btn text-xs"
                            data-testid="scan-mark-paid"
                        >
                            Mark paid
                        </button>
                        <button
                            onClick={() => onPaymentUpdate(result.registration, "pay_at_door")}
                            className="pm-btn text-xs"
                            data-testid="scan-mark-unpaid"
                        >
                            Mark unpaid
                        </button>
                    </div>
                </div>
            )}

            <div className="mt-5 flex gap-2">
                <button onClick={onScanNext} className="pm-btn pm-btn-primary flex-1" data-testid="scan-next-btn">
                    <ScanLine className="w-4 h-4 mr-2" /> Scan next
                </button>
                <button onClick={onClose} className="pm-btn" data-testid="scan-close-btn">
                    <X className="w-4 h-4 mr-2" /> Close
                </button>
            </div>
        </div>
    );
}

function Row({ label, value, testId }) {
    return (
        <div className="flex items-center justify-between" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase text-[#666]">{label}</div>
            <div className="font-display font-bold uppercase text-sm text-right">{value}</div>
        </div>
    );
}

function StatBox({ label, value, highlight, testId }) {
    return (
        <div className="bg-[#0a0a0a] p-5" data-testid={testId}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#666]">{label}</div>
            <div className={`mt-2 font-display font-black uppercase tracking-tighter text-3xl md:text-4xl ${highlight ? "text-[#00e676]" : ""}`}>{value}</div>
        </div>
    );
}
